import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Stack, Alert, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material';
import { SignJWT } from 'jose';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function JWTGenerator() {
  const [header, setHeader] = useState('{\n  "alg": "HS256",\n  "typ": "JWT"\n}');
  const [payload, setPayload] = useState('{\n  "sub": "1234567890",\n  "name": "John Doe",\n  "iat": 1516239022\n}');
  const [secret, setSecret] = useState('your-256-bit-secret');
  const [algorithm, setAlgorithm] = useState('HS256');
  const [jwt, setJwt] = useState('');
  const [error, setError] = useState('');

  const formatJSON = (jsonString, setter) => {
    try {
      const parsed = JSON.parse(jsonString);
      setter(JSON.stringify(parsed, null, 2));
      setError('');
    } catch (e) {
      setError('Invalid JSON format.');
    }
  };

  const generateJWT = async () => {
    setError('');
    try {
      const parsedHeader = JSON.parse(header);
      const parsedPayload = JSON.parse(payload);

      // Verify algorithm matches
      if (parsedHeader.alg !== algorithm) {
        parsedHeader.alg = algorithm;
        setHeader(JSON.stringify(parsedHeader, null, 2));
      }

      const encoder = new TextEncoder();
      const key = encoder.encode(secret);

      const token = await new SignJWT(parsedPayload)
        .setProtectedHeader(parsedHeader)
        .sign(key);

      setJwt(token);
    } catch (e) {
      setError(e.message || 'Error generating JWT. Ensure Header and Payload are valid JSON.');
      setJwt('');
    }
  };

  useEffect(() => {
    generateJWT();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header, payload, secret, algorithm]);

  const handleCopy = () => {
    if (jwt) {
      navigator.clipboard.writeText(jwt);
    }
  };

  return (
    <>
      <SEO
        title="JWT Generator | Create JSON Web Tokens | UmbrellaCo"
        description="Generate standard JSON Web Tokens (JWT) online. Customize your header, payload, and securely sign tokens with HS256, HS384, or HS512 algorithms."
        keywords="jwt generator, create jwt token, generate json web token, jwt encoder, jwt signing"
        url="/dev-tools/jwt-generator"
        jsonLd={createSoftwareAppSchema(
          'JWT Generator',
          'Generate and sign standard JSON Web Tokens securely.',
          'https://umbrellaco.in/dev-tools/jwt-generator',
          ['JWT Generation', 'Token Signing', 'Developer Security Tools']
        )}
      />
      <ToolPageContainer
        title="JWT Generator"
        description="Craft and sign JSON Web Tokens (JWT) for testing APIs and authentication flows."
        disablePadding
      >
      <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ flexGrow: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, minHeight: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', pr: 1 }}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" color="error.main">Header</Typography>
              <Button size="small" onClick={() => formatJSON(header, setHeader)} startIcon={<FormatAlignLeftIcon />}>Format</Button>
            </Stack>
            <TextField
              multiline
              rows={4}
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ fontFamily: 'monospace' }}
            />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" color="secondary.main">Payload (Data)</Typography>
              <Button size="small" onClick={() => formatJSON(payload, setPayload)} startIcon={<FormatAlignLeftIcon />}>Format</Button>
            </Stack>
            <TextField
              multiline
              rows={8}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ fontFamily: 'monospace' }}
            />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="info.main" sx={{ mb: 1 }}>Verify Signature</Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Algorithm</InputLabel>
                <Select
                  value={algorithm}
                  label="Algorithm"
                  onChange={(e) => setAlgorithm(e.target.value)}
                >
                  <MenuItem value="HS256">HS256</MenuItem>
                  <MenuItem value="HS384">HS384</MenuItem>
                  <MenuItem value="HS512">HS512</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Secret Key"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                variant="outlined"
                fullWidth
                sx={{ fontFamily: 'monospace' }}
              />
            </Stack>
          </Paper>
        </Box>

        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', bgcolor: mode => mode.palette === 'dark' ? 'grey.900' : 'grey.100' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Generated Token</Typography>
            <Button variant="contained" onClick={handleCopy} startIcon={<ContentCopyIcon />} disabled={!jwt}>Copy Token</Button>
          </Stack>
          <Box sx={{ flexGrow: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1, overflowWrap: 'anywhere', fontFamily: 'monospace', fontSize: '1.1rem', wordBreak: 'break-all', overflowY: 'auto' }}>
            {jwt ? (
              <>
                <Typography component="span" sx={{ color: 'error.main' }}>{jwt.split('.')[0]}</Typography>
                <Typography component="span" sx={{ color: 'text.primary' }}>.</Typography>
                <Typography component="span" sx={{ color: 'secondary.main' }}>{jwt.split('.')[1]}</Typography>
                <Typography component="span" sx={{ color: 'text.primary' }}>.</Typography>
                <Typography component="span" sx={{ color: 'info.main' }}>{jwt.split('.')[2]}</Typography>
              </>
            ) : (
              <Typography color="text.secondary">Invalid input. Fix errors to generate token.</Typography>
            )}
          </Box>
        </Paper>
        </Box>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default JWTGenerator;
